import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MiniGameService } from '../../../_services/mini-game.service';
import { GameModeService } from '../../../_services/game-mode.service';
import {
  MiniGameSession, MiniGameType, MiniGameStudent, StudentScoreEntry,
  TugOfWarState, LightningRoundState, BombPassState, SpinWheelState
} from '../../../interfaces/mini-game.interface';
import { ClassCreature } from '../../../interfaces/creature.interface';
import { GlobalService } from 'src/app/_services/global.service';
import { GameAudioService } from '../../../_services/game-audio.service';

const AUTO_ADVANCE_DELAY = 15; // seconds before auto-advancing to next game
const TRANSITION_DURATION = 3; // seconds for the "next game" transition screen

@Component({
  selector: 'app-mini-game-summary',
  templateUrl: './mini-game-summary.component.html',
  styleUrls: ['./mini-game-summary.component.scss']
})
export class MiniGameSummaryComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  currLang: any = 'en';

  session: MiniGameSession | null = null;
  creature: ClassCreature | null = null;
  MiniGameType = MiniGameType;

  // Podium
  topStudents: StudentScoreEntry[] = [];
  podiumPhase = 0; // 0=hidden, 1=3rd center, 2=3rd→right+2nd center, 3=2nd→left+1st center, 4=settled+confetti
  confettiPieces: { x: string; delay: string; duration: string; color: string; rotation: string }[] = [];

  // Auto-cycle state
  autoCycleEnabled = false;
  autoAdvanceCountdown = 0;
  showTransition = false;
  nextGameName = '';
  nextGameIcon = '';
  private autoAdvanceInterval: any;
  private pendingTimeouts: any[] = [];
  private destroyed = false;
  private applauseAudio: HTMLAudioElement | null = null;

  constructor(
    private miniGameService: MiniGameService,
    private gameModeService: GameModeService,
    private globalService: GlobalService,
    private gameAudioService: GameAudioService
  ) {}

  ngOnInit(): void {
    this.globalService.getLang()
      .pipe(takeUntil(this.destroy$))
      .subscribe(lang => this.currLang = lang);

    this.miniGameService.session$
      .pipe(takeUntil(this.destroy$))
      .subscribe(session => this.session = session);

    this.gameModeService.creature$
      .pipe(takeUntil(this.destroy$))
      .subscribe(creature => this.creature = creature);

    // Podium: get top 3 ranked students
    this.miniGameService.studentScores$
      .pipe(takeUntil(this.destroy$))
      .subscribe(scores => {
        this.topStudents = scores
          .filter(s => s.totalAnswered > 0)
          .sort((a, b) => b.xp - a.xp || b.correctCount - a.correctCount)
          .slice(0, 3);
        if (this.topStudents.length > 0 && this.session?.gameType !== MiniGameType.TUG_OF_WAR) {
          this.startPodiumReveal();
        }
      });

    this.applauseAudio = this.gameAudioService.play(
      'assets/game-mode/sound-effects/applauseSoundEffect.mp3',
      { loop: true }
    );

    // Auto-cycle: start countdown to next game
    this.miniGameService.autoCycleEnabled$
      .pipe(takeUntil(this.destroy$))
      .subscribe(enabled => {
        this.autoCycleEnabled = enabled;
        if (enabled) {
          this.startAutoAdvanceCountdown();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.pendingTimeouts.forEach(t => clearTimeout(t));
    this.pendingTimeouts = [];
    this.clearAutoAdvance();
    this.gameAudioService.stop(this.applauseAudio);
    this.applauseAudio = null;
    this.destroy$.next();
    this.destroy$.complete();
  }

  private safeTimeout(fn: () => void, ms: number): void {
    const id = setTimeout(() => {
      if (!this.destroyed) fn();
    }, ms);
    this.pendingTimeouts.push(id);
  }

  private startAutoAdvanceCountdown(): void {
    this.autoAdvanceCountdown = AUTO_ADVANCE_DELAY;
    const nextGame = this.miniGameService.getNextGameConfig();
    if (nextGame) {
      this.nextGameName = this.currLang === 'ar' ? nextGame.name.ar : nextGame.name.en;
      this.nextGameIcon = nextGame.icon;
    }

    this.autoAdvanceInterval = setInterval(() => {
      this.autoAdvanceCountdown--;
      if (this.autoAdvanceCountdown <= 0) {
        this.clearAutoAdvance();
        this.showTransitionScreen();
      }
    }, 1000);
  }

  private showTransitionScreen(): void {
    this.showTransition = true;
    this.safeTimeout(() => {
      this.showTransition = false;
      this.backToHub();
    }, TRANSITION_DURATION * 1000);
  }

  private clearAutoAdvance(): void {
    if (this.autoAdvanceInterval) {
      clearInterval(this.autoAdvanceInterval);
      this.autoAdvanceInterval = null;
    }
  }

  get correctCount(): number {
    return this.session?.answers.filter(a => a.isCorrect).length || 0;
  }

  get totalCount(): number {
    return this.session?.answers.length || 0;
  }

  get accuracy(): number {
    return this.totalCount ? Math.round((this.correctCount / this.totalCount) * 100) : 0;
  }

  get totalXP(): number {
    return this.session?.totalXP || 0;
  }

  get gameName(): string {
    if (!this.session) return '';
    return this.currLang === 'ar' ? this.session.config.name.ar : this.session.config.name.en;
  }

  get gameIcon(): string {
    return this.session?.config.icon || '';
  }

  // --- Game-specific stats ---

  get tugWinner(): string {
    if (!this.session || this.session.gameType !== MiniGameType.TUG_OF_WAR) return '';
    const state = this.session.gameState as TugOfWarState;
    if (state.ropePosition < 0) {
      return this.currLang === 'ar' ? this.session.teams!.teamA.name.ar : this.session.teams!.teamA.name.en;
    } else if (state.ropePosition > 0) {
      return this.currLang === 'ar' ? this.session.teams!.teamB.name.ar : this.session.teams!.teamB.name.en;
    }
    return this.currLang === 'ar' ? 'تعادل!' : 'Tie!';
  }

  get maxCombo(): number {
    if (!this.session || this.session.gameType !== MiniGameType.LIGHTNING_ROUND) return 0;
    return (this.session.gameState as LightningRoundState).maxCombo;
  }

  get bombMVP(): string {
    if (!this.session || this.session.gameType !== MiniGameType.BOMB_PASS) return '';
    const state = this.session.gameState as BombPassState;
    const explosionCounts: Record<number, number> = {};
    state.passOrder.forEach(id => explosionCounts[id] = 0);
    state.explosions.forEach(e => explosionCounts[e.studentId] = (explosionCounts[e.studentId] || 0) + 1);

    let mvpId = state.passOrder[0];
    let minExplosions = Infinity;
    for (const id of state.passOrder) {
      if ((explosionCounts[id] || 0) < minExplosions) {
        minExplosions = explosionCounts[id] || 0;
        mvpId = id;
      }
    }

    const student = this.session.students.find(s => s.id === mvpId);
    if (!student) return '';
    return this.currLang === 'ar' ? student.name.ar : student.name.en;
  }

  get spinWheelBest(): string {
    if (!this.session || this.session.gameType !== MiniGameType.SPIN_WHEEL) return '';
    const state = this.session.gameState as SpinWheelState;
    let bestId = 0;
    let bestRatio = -1;
    for (const r of state.studentResults) {
      if (r.total > 0) {
        const ratio = r.correct / r.total;
        if (ratio > bestRatio) {
          bestRatio = ratio;
          bestId = r.studentId;
        }
      }
    }
    const student = this.session.students.find(s => s.id === bestId);
    if (!student) return '';
    return this.currLang === 'ar' ? student.name.ar : student.name.en;
  }

  getPerformanceEmoji(): string {
    if (this.accuracy >= 90) return '&#127942;';
    if (this.accuracy >= 70) return '&#11088;';
    if (this.accuracy >= 50) return '&#128170;';
    return '&#128161;';
  }

  backToHub(): void {
    this.miniGameService.finishMiniGame();
  }

  getStudentName(student: MiniGameStudent): string {
    const name = this.currLang === 'ar' ? student.name?.ar : student.name?.en;
    if (!name) return '';
    const first = name.split(' ')[0];
    return first.length > 14 ? first.slice(0, 13) + '.' : first;
  }

  private startPodiumReveal(): void {
    this.podiumPhase = 0;
    const has3 = this.topStudents.length >= 3;
    const has2 = this.topStudents.length >= 2;

    // Phase 1: 3rd place appears center
    this.safeTimeout(() => { if (has3) this.podiumPhase = 1; else if (has2) this.podiumPhase = 2; else this.podiumPhase = 3; }, 300);
    // Phase 2: 3rd slides right, 2nd appears center
    if (has3) this.safeTimeout(() => this.podiumPhase = 2, 1600);
    // Phase 3: 2nd slides left, 1st appears center
    this.safeTimeout(() => this.podiumPhase = 3, has3 ? 2900 : has2 ? 1600 : 300);
    // Phase 4: settled, confetti
    this.safeTimeout(() => { this.podiumPhase = 4; this.generateConfetti(); }, has3 ? 3800 : has2 ? 2500 : 1200);
  }

  private generateConfetti(): void {
    const colors = ['#FFD700', '#E91E63', '#4CAF50', '#2196F3', '#FF9800', '#9C27B0', '#00BCD4'];
    this.confettiPieces = Array.from({ length: 80 }, () => ({
      x: Math.random() * 100 + '%',
      delay: (Math.random() * 2).toFixed(2) + 's',
      duration: (2.5 + Math.random() * 2).toFixed(2) + 's',
      color: colors[Math.floor(Math.random() * colors.length)],
      rotation: (Math.random() * 720 - 360).toFixed(0) + 'deg'
    }));
  }
}
